/**
 * Table Column Definitions - Single Source of Truth
 * 
 * All table columns are defined here to avoid duplication.
 * Each table has its own column array that can be imported and used.
 * 
 * IMPORTANT: Uses shared constants from constants.js for consistency
 * - WORK_TYPES: Used in RFQ, Job Details, Onboarding
 * - JOB_STATUSES: Used in RFQ list, Job Details, City Profile (single status source of truth)
 * - Contact types: Fetched from API /contact_settings (dynamic)
 */

import React from 'react';
import { getDueDateStyle, getStatusBadgeClass, getStatusText, normalizeDate } from '../components/DataTable/tableUtils';
import { WORK_TYPES, JOB_STATUSES, normalizeStatus } from '../constants';

// Helper function to get state for each city
export function getStateForCity(cityName) {
  const stateMap = {
    // Arizona
    'City of Apache Junction': 'AZ', 'City of Avondale': 'AZ', 'City of Benson': 'AZ',
    'City of Bisbee': 'AZ', 'City of Buckeye': 'AZ', 'City of Bullhead City': 'AZ',
    'City of Casa Grande': 'AZ', 'City of Chandler': 'AZ', 'City of Flagstaff': 'AZ',
    'City of Fountain Hills': 'AZ', 'City of Glendale': 'AZ', 'City of Goodyear': 'AZ',
    'City of Kingman': 'AZ', 'City of Lake Havasu City': 'AZ', 'City of Marana': 'AZ',
    'City of Maricopa': 'AZ', 'City of Mesa': 'AZ', 'City of Nogales': 'AZ',
    'City of Peoria': 'AZ', 'City of Phoenix': 'AZ', 'City of Prescott': 'AZ',
    'City of Prescott Valley': 'AZ', 'City of Safford': 'AZ', 'City of Scottsdale': 'AZ',
    'City of Sierra Vista': 'AZ', 'City of Surprise': 'AZ', 'City of Tempe': 'AZ',
    'City of Tucson': 'AZ', 'City of Yuma': 'AZ', 'Town of Carefree': 'AZ',
    'Town of Coolidge': 'AZ', 'Town of El Mirage': 'AZ', 'Town of Florence': 'AZ',
    'Town of Gilbert': 'AZ', 'Town of Litchfield Park': 'AZ', 'Town of Page': 'AZ',
    'Town of Paradise Valley': 'AZ', 'Town of Queen Creek': 'AZ', 'Town of Sahuarita': 'AZ',
    'Town of San Luis': 'AZ', 'Town of Youngtown': 'AZ',
    'Cochise County': 'AZ', 'Coconino County': 'AZ', 'Maricopa County': 'AZ',
    'Mohave County': 'AZ', 'Pima County': 'AZ', 'Pinal County': 'AZ',
    'Santa Cruz County': 'AZ', 'Yavapai County': 'AZ', 'Yuma County': 'AZ',
    'State of Arizona': 'AZ',
    // Colorado
    'City of Denver': 'CO', 'City of Colorado Springs': 'CO', 'City of Aurora': 'CO',
    'City of Englewood': 'CO',
    // New Mexico
    'City of Albuquerque': 'NM', 'City of Santa Fe': 'NM', 'City of Las Cruces': 'NM',
    // Florida
    'City of Miami': 'FL', 'City of Tampa': 'FL', 'City of Orlando': 'FL',
    // Georgia
    'City of Atlanta': 'GA', 'City of Savannah': 'GA',
    // Texas
    'City of Houston': 'TX', 'City of Dallas': 'TX', 'City of Austin': 'TX',
  };
  return stateMap[cityName] || 'OTHER';
}

// Helper function to detect platform from URL
export function getPlatformFromUrl(url) {
  if (!url) return 'Other';
  const urlLower = url.toLowerCase().trim();
  
  // Check for platform keywords in URL
  if (urlLower.includes('bonfire')) return 'Bonfire';
  
  // OpenGov has multiple patterns
  if (urlLower.includes('opengov') || 
      urlLower.includes('open-gov') || 
      urlLower.includes('open_gov')) {
    return 'OpenGov';
  }
  
  if (urlLower.includes('planetbids') || 
      urlLower.includes('planet-bids') || 
      urlLower.includes('planet_bids')) {
    return 'PlanetBids';
  }
  
  if (urlLower.includes('bids.aspx') || 
      urlLower.includes('civicplus') || 
      urlLower.includes('civic-plus') || 
      urlLower.includes('civic_plus')) {
    return 'CivicPlus';
  }
  
  return 'Other';
}

/**
 * RFQ Table Columns
 */
export const RFQ_COLUMNS = [
  {
    // Single source of truth for job status (business-development/BD_METRICS_SPEC.md Section 6e).
    // One canonical choice atomically drives user_status + outcome + pursuit_stage on the backend.
    accessorKey: 'user_status',
    id: 'user_status',
    header: 'Status',
    cell: ({ row, table }) => {
      const rawStatus = row.original.user_status || 'new';
      // Check if we're in editable mode (City Profile) or display mode (Main RFQ table)
      if (table.options.meta?.updateUserStatus) {
        return (
          <select
            className="form-select form-select-sm"
            value={normalizeStatus(rawStatus)}
            onChange={(e) => {
              e.stopPropagation();
              const newStatus = e.target.value;
              if (newStatus === 'no_go') {
                // No-Go requires a reason, which can only be picked on the Job Details page.
                // Least-complex option consistent with this table's existing patterns: route
                // the user to Job Details instead of half-saving an inline change here.
                if (table.options.meta?.onJobIdClick) {
                  alert('No-Go requires a reason. Opening Job Details so you can select one.');
                  table.options.meta.onJobIdClick(row.original.job_id);
                }
                return;
              }
              table.options.meta.updateUserStatus(row.original.job_id, newStatus);
            }}
            onClick={(e) => e.stopPropagation()}
            style={{ minWidth: '140px' }}
          >
            {JOB_STATUSES.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
            {rawStatus === 'completed' && (
              <option value="completed">Completed (legacy) — reassign to Won or Lost</option>
            )}
          </select>
        );
      }
      // Default: show badge
      return (
        <span
          className={getStatusBadgeClass(rawStatus)}
          title={rawStatus === 'completed' ? 'Legacy status - reassign to Won or Lost' : undefined}
        >
          {getStatusText(rawStatus)}
        </span>
      );
    },
    enableSorting: true,
    enableHiding: false, // Always visible
    size: 150,
  },
  {
    accessorKey: 'job_id',
    id: 'job_id',
    header: 'Job ID',
    cell: ({ row, table }) => {
      const jobId = row.original.job_id ? row.original.job_id.substring(0, 8) : 'N/A';
      
      // If onJobIdClick is provided in meta (City Profile or RFQ Table), make it clickable
      if (table.options.meta?.onJobIdClick) {
        return (
          <span 
            className="font-monospace text-primary"
            style={{ fontSize: '0.9rem', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={(e) => {
              e.stopPropagation();
              table.options.meta.onJobIdClick(row.original.job_id);
            }}
            title="View job details"
          >
            {jobId}
          </span>
        );
      }
      
      // Default: non-clickable
      return (
        <span className="font-monospace" style={{ fontSize: '0.9rem' }}>
          {jobId}
        </span>
      );
    },
    enableSorting: false,
    size: 100,
  },
  {
    accessorKey: 'organization',
    id: 'organization',
    header: 'Organization',
    cell: ({ row, table }) => (
      <div>
        <div>
          <a
            href={`/city/${encodeURIComponent(row.original.organization)}`}
            onClick={(e) => {
              e.stopPropagation();
              // Let the browser handle navigation
            }}
            className="text-decoration-none fw-bold"
          >
            {row.original.organization}
          </a>
        </div>
        {row.original.url && (
          <div>
            <a
              href={row.original.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-muted"
              style={{ fontSize: '0.85rem', textDecoration: 'none' }}
              title="View city procurement page"
            >
              🔗 Procurement Page
            </a>
          </div>
        )}
        {row.original.source === 'email' && (
          <span
            className="badge bg-success"
            style={{ fontSize: '10px' }}
            title={`Added from email on ${row.original.first_seen || 'unknown date'}`}
          >
            📧 Email
          </span>
        )}
      </div>
    ),
    enableSorting: true,
    size: 200,
  },
  {
    accessorKey: 'title',
    id: 'title',
    header: 'Title',
    cell: ({ row }) => (
      <div>
        <a
          href={row.original.link || '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none"
        >
          {row.original.title}
        </a>
        <br />
        <small className="text-muted">
          RFP #: {row.original.rfp_number || 'N/A'}
        </small>
      </div>
    ),
    enableSorting: true,
    size: 400,
  },
  {
    accessorKey: 'work_type',
    id: 'work_type',
    header: 'Work Type',
    cell: ({ row, table }) => (
      <select
        className="form-select form-select-sm"
        value={row.original.work_type || 'Unknown'}
        onChange={(e) => {
          // This will be passed as a prop to DataTable
          e.stopPropagation();
          if (table.options.meta?.updateWorkType) {
            table.options.meta.updateWorkType(row.original.job_id, e.target.value);
          }
        }}
        onClick={(e) => e.stopPropagation()}
        style={{ minWidth: '150px' }}
      >
        {WORK_TYPES.map(type => (
          <option key={type} value={type}>{type}</option>
        ))}
      </select>
    ),
    enableSorting: true,
    size: 180,
  },
  {
    accessorKey: 'due_date',
    id: 'due_date',
    header: 'Due Date',
    cell: ({ row }) => {
      const originalDate = row.original.due_date;
      const normalizedDate = normalizeDate(originalDate);
      
      return (
        <span 
          style={getDueDateStyle(originalDate)}
          title={originalDate} // Show full original date on hover
        >
          {normalizedDate}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: (rowA, rowB, columnId) => {
      // Custom sorting for dates - handled by TanStack Table
      const dateA = rowA.original.due_date;
      const dateB = rowB.original.due_date;
      
      // Import sortDates from tableUtils
      const { sortDates } = require('../components/DataTable/tableUtils');
      return sortDates(dateA, dateB);
    },
    size: 150,
  },
];

/**
 * RFQ Table Actions Column
 * Separate from main columns so it can be conditionally added
 */
export const getRFQActionsColumn = (updateUserStatus) => ({
  id: 'quick_actions',
  header: 'Quick Actions',
  cell: ({ row }) => (
    <div className="btn-group btn-group-sm" role="group">
      <button
        className="btn btn-outline-success"
        onClick={(e) => {
          e.stopPropagation();
          updateUserStatus(row.original.job_id, 'pursuing');
        }}
        title="Mark as Pursuing"
      >
        ✓
      </button>
      <button
        className="btn btn-outline-info"
        onClick={(e) => {
          e.stopPropagation();
          updateUserStatus(row.original.job_id, 'won');
        }}
        title="Mark as Won"
      >
        ✔✔
      </button>
      <button
        className="btn btn-outline-primary"
        onClick={(e) => {
          e.stopPropagation();
          updateUserStatus(row.original.job_id, 'watch');
        }}
        title="Watch"
      >
        👁️
      </button>
      <button
        className="btn btn-outline-dark"
        onClick={(e) => {
          e.stopPropagation();
          updateUserStatus(row.original.job_id, 'lost');
        }}
        title="Mark as Lost"
      >
        ✖
      </button>
      <button
        className="btn btn-outline-danger"
        onClick={(e) => {
          e.stopPropagation();
          updateUserStatus(row.original.job_id, 'irrelevant');
        }}
        title="Mark as Irrelevant (scraper noise)"
      >
        ✗
      </button>
    </div>
  ),
  enableSorting: false,
  enableHiding: false,
  size: 250,
});

/**
 * ===================================
 * Job Conflicts Table Columns
 * ===================================
 */

// Duplicate Job ID Conflicts Columns
export const DUPLICATE_CONFLICTS_COLUMNS = [
  {
    accessorKey: 'organization',
    header: 'Organization',
    enableSorting: true,
    size: 200,
  },
  {
    accessorKey: 'rfp_number',
    header: 'RFP #',
    enableSorting: true,
    size: 120,
  },
  {
    accessorKey: 'prod_job_id',
    header: 'Production Job ID',
    cell: ({ row }) => (
      <span className="prod-job-id" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
        {row.original.prod_job_id}
      </span>
    ),
    enableSorting: false,
    size: 180,
  },
  {
    accessorKey: 'dev_job_id',
    header: 'Dev Job ID',
    cell: ({ row }) => (
      <span className="dev-job-id" style={{ fontFamily: 'monospace', color: '#666' }}>
        {row.original.dev_job_id}
      </span>
    ),
    enableSorting: false,
    size: 180,
  },
  {
    accessorKey: 'city_url',
    header: 'City Page',
    cell: ({ row }) => (
      row.original.city_url ? (
        <a 
          href={row.original.city_url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="btn btn-sm btn-link"
          title="View city procurement page"
        >
          🔗 View
        </a>
      ) : (
        <span style={{ color: '#999' }}>N/A</span>
      )
    ),
    enableSorting: false,
    size: 100,
  },
  {
    accessorKey: 'detected_at',
    header: 'Detected',
    enableSorting: true,
    size: 150,
  },
];

// Field Change Conflicts Columns
export const FIELD_CONFLICTS_COLUMNS = [
  {
    accessorKey: 'organization',
    header: 'Organization',
    enableSorting: true,
    size: 160,
  },
  {
    accessorKey: 'rfp_number',
    header: 'RFP #',
    enableSorting: true,
    size: 80,
  },
  {
    accessorKey: 'link',
    header: 'Link',
    cell: ({ row }) => (
      row.original.link ? (
        <a 
          href={row.original.link} 
          target="_blank" 
          rel="noopener noreferrer"
          className="btn btn-sm btn-link p-0"
          title="Click to verify on actual RFQ website"
        >
          🔗
        </a>
      ) : (
        <span className="text-muted">-</span>
      )
    ),
    enableSorting: false,
    size: 50,
  },
  {
    accessorKey: 'field_name',
    header: 'Field',
    cell: ({ row }) => (
      <span className="field-name" style={{ fontWeight: 'bold' }}>
        {row.original.field_name.replace('_', ' ')}
      </span>
    ),
    enableSorting: true,
    size: 90,
  },
  {
    accessorKey: 'old_value',
    header: 'Current Value',
    cell: ({ row }) => (
      <div style={{ 
        backgroundColor: '#fff3cd', 
        color: '#000000',
        padding: '6px 8px', 
        borderRadius: '4px',
        lineHeight: '1.4',
        minHeight: '32px',
        display: 'flex',
        alignItems: 'center',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        fontSize: '0.9rem'
      }}>
        {row.original.old_value}
      </div>
    ),
    enableSorting: false,
    size: 250,
  },
  {
    accessorKey: 'new_value',
    header: 'New Value',
    cell: ({ row }) => (
      <div style={{ 
        backgroundColor: '#d1ecf1', 
        color: '#000000',
        padding: '6px 8px', 
        borderRadius: '4px',
        lineHeight: '1.4',
        minHeight: '32px',
        display: 'flex',
        alignItems: 'center',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        fontSize: '0.9rem'
      }}>
        {row.original.new_value}
      </div>
    ),
    enableSorting: false,
    size: 250,
  },
  {
    accessorKey: 'detected_at',
    header: 'Detected',
    cell: ({ row }) => (
      <span style={{ fontSize: '0.85rem' }}>
        {row.original.detected_at}
      </span>
    ),
    enableSorting: true,
    size: 120,
  },
];

/**
 * ===================================
 * New Sources Table Columns
 * ===================================
 */
export const NEW_SOURCES_COLUMNS = [
  {
    accessorKey: 'organization',
    header: 'Organization',
    cell: ({ row }) => (
      <strong>{row.original.organization}</strong>
    ),
    enableSorting: true,
    size: 200,
  },
  {
    accessorKey: 'job_count',
    header: 'Jobs Found',
    cell: ({ row }) => (
      <span className="badge bg-primary">{row.original.job_count}</span>
    ),
    enableSorting: true,
    size: 100,
  },
  {
    accessorKey: 'sample_link',
    header: 'Sample Job',
    cell: ({ row }) => (
      row.original.sample_link ? (
        <a 
          href={row.original.sample_link} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-decoration-none"
          title={row.original.sample_title}
        >
          🔗 {row.original.sample_title ? row.original.sample_title.substring(0, 40) + '...' : 'View Job'}
        </a>
      ) : (
        <span className="text-muted">N/A</span>
      )
    ),
    enableSorting: false,
    size: 250,
  },
  {
    accessorKey: 'first_rfq_date',
    header: 'First RFQ',
    enableSorting: true,
    size: 120,
  },
  {
    accessorKey: 'latest_rfq_date',
    header: 'Latest RFQ',
    enableSorting: true,
    size: 120,
  },
  {
    accessorKey: 'avg_confidence',
    header: 'Avg Confidence',
    cell: ({ row }) => {
      const confidence = row.original.avg_confidence;
      const percentage = (confidence * 100).toFixed(0);
      const badgeClass = confidence >= 0.85 ? 'success' : confidence >= 0.75 ? 'warning' : 'danger';
      return (
        <span className={`badge bg-${badgeClass}`}>
          {percentage}%
        </span>
      );
    },
    enableSorting: true,
    size: 120,
  },
  {
    accessorKey: 'recommendation',
    header: 'Recommendation',
    cell: ({ row }) => {
      const rec = row.original.recommendation;
      if (rec === '✅ Add Now') {
        return <span className="badge bg-success">{rec}</span>;
      } else if (rec === '⏳ Maybe Later') {
        return <span className="badge bg-warning text-dark">{rec}</span>;
      } else {
        return <span className="badge bg-secondary">{rec}</span>;
      }
    },
    enableSorting: true,
    size: 150,
  },
];

/**
 * ===================================
 * AI Monitoring - Email Logs Table Columns
 * ===================================
 */
export const EMAIL_LOGS_COLUMNS = [
  {
    accessorKey: 'success',
    header: 'Status',
    cell: ({ row }) => (
      <span style={{ fontSize: '1.2rem' }}>
        {row.original.success ? '✅' : '❌'}
      </span>
    ),
    enableSorting: true,
    size: 105,
    minSize: 90,
    enableHiding: false,
  },
  {
    accessorKey: 'processed_at',
    header: 'Processed At',
    cell: ({ row }) => {
      const date = new Date(row.original.processed_at);
      return date.toLocaleString('en-US', { 
        month: 'numeric', 
        day: 'numeric', 
        year: 'numeric', 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    },
    enableSorting: true,
    size: 160,
  },
  {
    accessorKey: 'sender',
    header: 'From',
    cell: ({ row }) => (
      <span style={{ fontSize: '0.9rem' }}>
        {row.original.sender}
      </span>
    ),
    enableSorting: true,
    size: 140,
  },
  {
    accessorKey: 'subject',
    header: 'Subject',
    cell: ({ row }) => (
      <span title={row.original.subject} style={{ fontSize: '0.9rem' }}>
        {row.original.subject?.substring(0, 40)}...
      </span>
    ),
    enableSorting: false,
    size: 250,
  },
  {
    accessorKey: 'jobs_added',
    header: 'Result',
    cell: ({ row }) => {
      if (row.original.success) {
        return (
          <span className="badge bg-success" style={{ fontSize: '0.85rem' }}>
            {row.original.jobs_added || 0} job(s) added
          </span>
        );
      } else {
        // Show error with a clickable badge that opens a modal
        const errorMsg = row.original.error_message || 'Unknown error';
        const shortError = errorMsg.length > 50 ? errorMsg.substring(0, 50) + '...' : errorMsg;
        
        return (
          <span 
            className="badge bg-danger" 
            style={{ fontSize: '0.85rem', cursor: 'pointer' }}
            title="Click to see full error"
            onClick={() => {
              // Show error in a modal that can be copied
              const modal = document.createElement('div');
              modal.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:9999;';
              modal.innerHTML = `
                <div style="background:white;padding:20px;border-radius:8px;max-width:800px;max-height:80vh;overflow:auto;position:relative;">
                  <button onclick="this.parentElement.parentElement.remove()" style="position:absolute;top:10px;right:10px;border:none;background:none;font-size:24px;cursor:pointer;">&times;</button>
                  <h5 style="margin-top:0;">Error Details</h5>
                  <div style="margin-bottom:10px;">
                    <strong>Subject:</strong> ${row.original.subject || 'N/A'}<br>
                    <strong>From:</strong> ${row.original.sender || 'N/A'}<br>
                    <strong>Processed:</strong> ${row.original.processed_at || 'N/A'}
                  </div>
                  <textarea 
                    readonly 
                    style="width:100%;min-height:300px;font-family:monospace;font-size:12px;border:1px solid #ccc;padding:10px;background:#f5f5f5;"
                  >${errorMsg}</textarea>
                  <button 
                    onclick="navigator.clipboard.writeText(this.previousElementSibling.value);this.innerText='Copied!';setTimeout(()=>this.innerText='Copy to Clipboard',2000)"
                    style="margin-top:10px;padding:8px 16px;background:#007bff;color:white;border:none;border-radius:4px;cursor:pointer;"
                  >Copy to Clipboard</button>
                </div>
              `;
              document.body.appendChild(modal);
              modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
            }}
          >
            {shortError}
          </span>
        );
      }
    },
    enableSorting: true,
    size: 155,
    minSize: 140,
  },
  {
    accessorKey: 'ai_confidence',
    header: 'Confidence',
    cell: ({ row }) => {
      if (!row.original.ai_confidence) return '-';
      const confidence = row.original.ai_confidence;
      const badgeClass = confidence >= 0.85 ? 'bg-success' : confidence >= 0.75 ? 'bg-warning' : 'bg-danger';
      return (
        <span className={`badge ${badgeClass}`} style={{ fontSize: '0.85rem' }}>
          {(confidence * 100).toFixed(0)}%
        </span>
      );
    },
    enableSorting: true,
    size: 140,
    minSize: 120,
  },
  {
    accessorKey: 'cost_usd',
    header: 'Cost',
    cell: ({ row }) => (
      <span style={{ fontSize: '0.9rem' }}>
        ${(row.original.cost_usd || 0).toFixed(4)}
      </span>
    ),
    enableSorting: true,
    size: 90,
  },
];

/**
 * ===================================
 * AI Monitoring - Validation Queue Table Columns
 * ===================================
 */
export const VALIDATION_QUEUE_COLUMNS = [
  {
    accessorKey: 'organization',
    header: 'Organization',
    enableSorting: true,
    size: 180,
  },
  {
    accessorKey: 'title',
    header: 'Title',
    cell: ({ row }) => (
      <span title={row.original.title}>
        {row.original.title?.substring(0, 60)}...
      </span>
    ),
    enableSorting: false,
    size: 300,
  },
  {
    accessorKey: 'due_date',
    header: 'Due Date',
    enableSorting: true,
    size: 150,
  },
  {
    accessorKey: 'rfp_number',
    header: 'RFP #',
    enableSorting: false,
    size: 120,
  },
  {
    accessorKey: 'issues',
    header: 'Issues Found',
    cell: ({ row }) => {
      const issues = row.original.issues || [];
      return (
        <ul className="mb-0 ps-3" style={{ fontSize: '0.85rem' }}>
          {issues.map((issue, idx) => (
            <li key={idx}>{issue}</li>
          ))}
        </ul>
      );
    },
    enableSorting: false,
    size: 250,
  },
  {
    accessorKey: 'confidence',
    header: 'Confidence',
    cell: ({ row }) => {
      const confidence = row.original.confidence;
      const badgeClass = confidence >= 0.75 ? 'bg-warning' : 'bg-danger';
      return (
        <span className={`badge ${badgeClass}`}>
          {(confidence * 100).toFixed(0)}%
        </span>
      );
    },
    enableSorting: true,
    size: 120,
  },
  {
    accessorKey: 'source',
    header: 'Source',
    cell: ({ row }) => (
      <span className="badge bg-info">
        {row.original.source === 'email' ? '📧 Email' : '🌐 Scraper'}
      </span>
    ),
    enableSorting: true,
    size: 100,
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString(),
    enableSorting: true,
    size: 120,
  },
];

/**
 * ===================================
 * User Management Table Columns
 * ===================================
 */
export const USER_MANAGEMENT_COLUMNS = [
  {
    accessorKey: 'username',
    header: 'Username',
    cell: ({ row, table }) => (
      <div>
        <strong>{row.original.username}</strong>
        {table.options.meta?.currentUsername === row.original.username && (
          <span className="badge bg-info ms-2">You</span>
        )}
      </div>
    ),
    enableSorting: true,
    size: 150,
  },
  {
    accessorKey: 'role',
    header: 'Role',
    cell: ({ row }) => {
      const getRoleBadgeClass = (role) => {
        if (role === 'dev') return 'bg-danger';
        if (role === 'admin') return 'bg-primary';
        if (role === 'manager') return 'bg-success';
        return 'bg-secondary';
      };
      
      const getRoleBadge = (role) => {
        if (role === 'dev') return '🔧 Dev';
        if (role === 'admin') return '👑 Admin';
        if (role === 'manager') return '📊 Manager';
        return '👁️ Viewer';
      };
      
      return (
        <span className={`badge ${getRoleBadgeClass(row.original.role)}`}>
          {getRoleBadge(row.original.role)}
        </span>
      );
    },
    enableSorting: true,
    size: 120,
  },
  {
    accessorKey: 'full_name',
    header: 'Full Name',
    cell: ({ row }) => row.original.full_name || '-',
    enableSorting: true,
    size: 180,
  },
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row }) => row.original.email || '-',
    enableSorting: true,
    size: 200,
  },
  {
    accessorKey: 'assigned_region',
    header: 'Region 📍',
    cell: ({ row }) => row.original.assigned_region || 'ALL',
    enableSorting: true,
    size: 100,
  },
  {
    accessorKey: 'is_active',
    header: 'Status',
    cell: ({ row }) => (
      <span className={`badge ${row.original.is_active ? 'bg-success' : 'bg-secondary'}`}>
        {row.original.is_active ? 'Active' : 'Inactive'}
      </span>
    ),
    enableSorting: true,
    size: 100,
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString(),
    enableSorting: true,
    size: 120,
  },
  {
    accessorKey: 'last_login',
    header: 'Last Login',
    cell: ({ row }) => (
      row.original.last_login 
        ? new Date(row.original.last_login).toLocaleString()
        : 'Never'
    ),
    enableSorting: true,
    size: 180,
  },
];

/**
 * Helper function to create contacts filter config with dynamic contact types
 * Call this after fetching contact settings from API
 * 
 * @param {object} settings - Contact settings from /contact_settings API
 * @param {array} cities - List of cities for filter dropdown
 * @returns {object} Filter configuration for contacts table
 */
export function getContactsFilterConfig(settings, cities) {
  return {
    searchTerm: {
      type: 'text',
      label: 'Search',
      placeholder: 'Search name, company, email...',
      className: 'col-md-4',
    },
    contact_type: {
      type: 'select',
      label: 'Contact Type',
      options: (settings.contact_types || []).map(type => ({ value: type, label: type })),
      allLabel: 'All Types',
      className: 'col-md-3',
    },
    associated_city: {
      type: 'select',
      label: 'City',
      options: cities.map(city => ({ value: city.name, label: city.name })),
      allLabel: 'All Cities',
      className: 'col-md-3',
    },
  };
}

/**
 * Cities Table Columns
 */
export const CITIES_COLUMNS = [
  {
    accessorKey: 'name',
    id: 'name',
    header: 'City/Organization',
    cell: ({ row }) => (
      <div>
        <button 
          className="btn btn-link p-0 text-decoration-none"
          style={{ textAlign: 'left' }}
        >
          {row.original.name}
        </button>
        {(row.original.manual || !row.original.is_active) && (
          <span 
            className="badge bg-warning text-dark ms-2"
            title="This city requires manual job entry"
          >
            ⚠️ Manual
          </span>
        )}
      </div>
    ),
    enableSorting: true,
    size: 350, // Wider for better readability
  },
  {
    accessorKey: 'state',
    id: 'state',
    header: 'State',
    cell: ({ row }) => (
      <span style={{ fontWeight: 'bold', textAlign: 'center', display: 'block' }}>
        {getStateForCity(row.original.name)}
      </span>
    ),
    enableSorting: true,
    size: 100,
  },
  {
    accessorKey: 'url',
    id: 'url',
    header: 'Procurement Page',
    cell: ({ row }) => (
      <div style={{ textAlign: 'center' }}>
        {row.original.url ? (
          <a 
            href={row.original.url} 
            target="_blank" 
            rel="noopener noreferrer"
            title={`Open ${row.original.name} procurement page`}
            style={{ fontSize: '1.2em', textDecoration: 'none' }}
            onClick={(e) => e.stopPropagation()}
          >
            🔗
          </a>
        ) : '—'}
      </div>
    ),
    enableSorting: false,
    size: 150,
  },
  {
    accessorKey: 'job_count',
    id: 'job_count',
    header: 'Jobs',
    cell: ({ row }) => (
      <div style={{ textAlign: 'center' }}>
        {row.original.job_count || 0}
      </div>
    ),
    enableSorting: true,
    size: 100,
  },
  {
    accessorKey: 'created_at',
    id: 'created_at',
    header: 'Date Added',
    cell: ({ row }) => (
      row.original.created_at 
        ? new Date(row.original.created_at).toLocaleDateString()
        : '—'
    ),
    enableSorting: true,
    size: 150,
  },
  {
    accessorKey: 'last_scraped',
    id: 'last_scraped',
    header: 'Last Scraped',
    cell: ({ row }) => {
      if (!row.original.last_scraped) return '—';
      
      const lastScraped = new Date(row.original.last_scraped);
      const now = new Date();
      const hoursSince = Math.floor((now - lastScraped) / (1000 * 60 * 60));
      
      let color = '#dc3545'; // Red (default for old)
      let fontWeight = 'bold';
      
      if (hoursSince <= 24) {
        color = '#28a745'; // Green (< 24 hours)
        fontWeight = 'normal';
      } else if (hoursSince <= 48) {
        color = '#ffc107'; // Yellow (24-48 hours)
        fontWeight = 'normal';
      }
      
      return (
        <span style={{ color, fontWeight }}>
          {lastScraped.toLocaleDateString()}
          {hoursSince <= 24 && (
            <span style={{ fontSize: '0.85em', marginLeft: '4px' }}>
              ({hoursSince}h ago)
            </span>
          )}
        </span>
      );
    },
    enableSorting: true,
    size: 180,
  },
  {
    accessorKey: 'is_active',
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const isActive = row.original.is_active !== false;
      return (
        <div style={{ textAlign: 'center' }}>
          <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`}>
            {isActive ? '✅ Active' : '⏸️ Paused'}
          </span>
        </div>
      );
    },
    enableSorting: true,
    size: 130,
  },
];

/**
 * Contacts Table Columns
 */
export const CONTACTS_COLUMNS = [
  {
    accessorKey: 'name',
    id: 'name',
    header: 'Name',
    cell: ({ row }) => <strong>{row.original.name}</strong>,
    enableSorting: true,
    size: 200,
  },
  {
    accessorKey: 'company_name',
    id: 'company_name',
    header: 'Company',
    cell: ({ row }) => row.original.company_name || '—',
    enableSorting: true,
    size: 200,
  },
  {
    accessorKey: 'contact_type',
    id: 'contact_type',
    header: 'Type',
    cell: ({ row }) => (
      row.original.contact_type ? (
        <span className="badge bg-secondary">{row.original.contact_type}</span>
      ) : '—'
    ),
    enableSorting: true,
    size: 150,
  },
  {
    accessorKey: 'phone',
    id: 'phone',
    header: 'Phone',
    cell: ({ row }) => row.original.phone || '—',
    enableSorting: false,
    size: 150,
  },
  {
    accessorKey: 'email',
    id: 'email',
    header: 'Email',
    cell: ({ row }) => (
      row.original.email ? (
        <a 
          href={`mailto:${row.original.email}`} 
          onClick={(e) => e.stopPropagation()}
          className="text-decoration-none"
        >
          {row.original.email}
        </a>
      ) : '—'
    ),
    enableSorting: true,
    size: 250,
  },
  {
    accessorKey: 'created_at',
    id: 'created_at',
    header: 'Added',
    cell: ({ row }) => (
      <span className="text-muted">
        {new Date(row.original.created_at).toLocaleDateString()}
      </span>
    ),
    enableSorting: true,
    size: 120,
  },
];

/**
 * Job Conflicts Table Columns
 */
export const JOB_CONFLICTS_COLUMNS = [
  {
    accessorKey: 'organization',
    header: 'Organization',
    enableSorting: true,
    size: 200,
  },
  {
    accessorKey: 'title',
    header: 'Title',
    enableSorting: true,
    size: 300,
  },
  {
    accessorKey: 'field_name',
    header: 'Conflict Field',
    enableSorting: true,
    size: 150,
  },
  {
    accessorKey: 'dev_value',
    header: 'Dev Value',
    cell: ({ row }) => (
      <span className="badge bg-info">{row.original.dev_value || 'N/A'}</span>
    ),
    size: 200,
  },
  {
    accessorKey: 'prod_value',
    header: 'Prod Value',
    cell: ({ row }) => (
      <span className="badge bg-warning">{row.original.prod_value || 'N/A'}</span>
    ),
    size: 200,
  },
  {
    accessorKey: 'detected_at',
    header: 'Detected',
    cell: ({ row }) => {
      if (!row.original.detected_at) return 'N/A';
      return new Date(row.original.detected_at).toLocaleString();
    },
    enableSorting: true,
    size: 180,
  },
];

